import {
  CoreLanguageType,
  LanguageType,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';
import { RegionCode } from 'shared/src/types/region.type';

type PriceLocaleRow = {
  suggestedPriceLabel: string;
    liveDealLabel: string;
    blackFridayDealLabel: string;
    boxingDayDealLabel: string;
    from: string;
    priceAlerts: {
      notAvailable: string;
      signInToGet: string;
      turnOff: string;
      getNotified: string;
      title: string;
      neverMissADeal: string;
      getNotifiedWhen: string;
      goesOnSaleDuring: string;
      continueWithGoogle: string;
      instantNotifications: string;
      exclusiveDeals: string;
      noSpam: string;
      agreementText: string;
      unsubscribeText: string;
      successSubscribed: string;
      successUnsubscribed: string;
      successSubscribedMessage: string;
      successUnsubscribedMessage: string;
      emailConfirmation: string;
      checkYourEmail: string;
      errorMessage: string;
    };
};

const priceUiContentCore: Record<CoreLanguageType, PriceLocaleRow> = {
  en: {
    suggestedPriceLabel: 'Suggested Price',
    liveDealLabel: 'Live Deal',
    blackFridayDealLabel: 'Black Friday Deal',
    boxingDayDealLabel: 'Boxing Day Deal',
    from: 'From',
    priceAlerts: {
      notAvailable: 'Price alerts not available',
      signInToGet: 'Sign in to get {eventName} price alerts',
      turnOff: 'Turn off {eventName} price alerts',
      getNotified: 'Get notified of {eventName} deals',
      title: '{eventName} Price Alert',
      neverMissADeal: 'Never miss a deal again',
      getNotifiedWhen: 'Get notified the moment',
      goesOnSaleDuring: 'goes on sale during',
      continueWithGoogle: 'Continue with Google',
      instantNotifications: 'Instant notifications when prices drop',
      exclusiveDeals: 'Exclusive {eventType} deals',
      noSpam: 'No spam, only the deals you want',
      agreementText: 'By continuing, you agree to receive price alerts for this product.',
      unsubscribeText: 'You can unsubscribe at any time.',
      successSubscribed: "You're All Set!",
      successUnsubscribed: 'Unsubscribed Successfully',
      successSubscribedMessage: "We'll notify you when {productTitle} goes on sale during {eventName}",
      successUnsubscribedMessage: "You won't receive notifications for {productTitle} during {eventName}",
      emailConfirmation: 'Check your email for confirmation',
      checkYourEmail: '💡 Check your email for confirmation',
      errorMessage: 'Failed to update price alert subscription',
    },
  },
  fr: {
    suggestedPriceLabel: 'Prix suggéré',
    liveDealLabel: 'Offre en direct',
    blackFridayDealLabel: 'Offre Black Friday',
    boxingDayDealLabel: 'Offre Boxing Day',
    from: 'À partir de',
    priceAlerts: {
      notAvailable: 'Alertes de prix non disponibles',
      signInToGet: 'Connectez-vous pour recevoir les alertes {eventName}',
      turnOff: 'Désactiver les alertes {eventName}',
      getNotified: 'Être notifié des offres {eventName}',
      title: 'Alerte prix {eventName}',
      neverMissADeal: 'Ne ratez plus jamais une offre',
      getNotifiedWhen: 'Soyez notifié dès que',
      goesOnSaleDuring: 'est en promotion pendant',
      continueWithGoogle: 'Continuer avec Google',
      instantNotifications: 'Notifications instantanées lors de baisses de prix',
      exclusiveDeals: 'Offres exclusives {eventType}',
      noSpam: 'Pas de spam, seulement les offres que vous voulez',
      agreementText: 'En continuant, vous acceptez de recevoir des alertes de prix pour ce produit.',
      unsubscribeText: 'Vous pouvez vous désabonner à tout moment.',
      successSubscribed: 'Tout est prêt !',
      successUnsubscribed: 'Désabonnement réussi',
      successSubscribedMessage: 'Nous vous préviendrons quand {productTitle} sera en promotion pendant {eventName}',
      successUnsubscribedMessage: 'Vous ne recevrez plus de notifications pour {productTitle} pendant {eventName}',
      emailConfirmation: 'Vérifiez votre email pour confirmation',
      checkYourEmail: '💡 Vérifiez votre email pour confirmation',
      errorMessage: "Échec de la mise à jour de l'alerte de prix",
    },
  },
  de: {
    suggestedPriceLabel: 'Empfohlener Preis',
    liveDealLabel: 'Live-Angebot',
    blackFridayDealLabel: 'Black Friday Angebot',
    boxingDayDealLabel: 'Boxing Day Angebot',
    from: 'Ab',
    priceAlerts: {
      notAvailable: 'Preisalarme nicht verfügbar',
      signInToGet: 'Anmelden für {eventName} Preisalarme',
      turnOff: '{eventName} Preisalarme deaktivieren',
      getNotified: 'Über {eventName} Angebote benachrichtigt werden',
      title: '{eventName} Preisalarm',
      neverMissADeal: 'Verpassen Sie nie wieder ein Angebot',
      getNotifiedWhen: 'Werden Sie benachrichtigt, sobald',
      goesOnSaleDuring: 'im Angebot ist während',
      continueWithGoogle: 'Mit Google fortfahren',
      instantNotifications: 'Sofortige Benachrichtigungen bei Preissenkungen',
      exclusiveDeals: 'Exklusive {eventType} Angebote',
      noSpam: 'Kein Spam, nur die gewünschten Angebote',
      agreementText: 'Durch Fortfahren stimmen Sie zu, Preisalarme für dieses Produkt zu erhalten.',
      unsubscribeText: 'Sie können sich jederzeit abmelden.',
      successSubscribed: 'Alles bereit!',
      successUnsubscribed: 'Erfolgreich abgemeldet',
      successSubscribedMessage: 'Wir benachrichtigen Sie, wenn {productTitle} während {eventName} im Angebot ist',
      successUnsubscribedMessage: 'Sie erhalten keine Benachrichtigungen mehr für {productTitle} während {eventName}',
      emailConfirmation: 'Überprüfen Sie Ihre E-Mail zur Bestätigung',
      checkYourEmail: '💡 Überprüfen Sie Ihre E-Mail zur Bestätigung',
      errorMessage: 'Preisalarm-Abonnement konnte nicht aktualisiert werden',
    },
  },
  es: {
    suggestedPriceLabel: 'Precio sugerido',
    liveDealLabel: 'Oferta en vivo',
    blackFridayDealLabel: 'Oferta Black Friday',
    boxingDayDealLabel: 'Oferta Boxing Day',
    from: 'Desde',
    priceAlerts: {
      notAvailable: 'Alertas de precio no disponibles',
      signInToGet: 'Inicia sesión para recibir alertas {eventName}',
      turnOff: 'Desactivar alertas {eventName}',
      getNotified: 'Recibir notificaciones de ofertas {eventName}',
      title: 'Alerta de precio {eventName}',
      neverMissADeal: 'Nunca te pierdas una oferta',
      getNotifiedWhen: 'Recibe notificación en el momento que',
      goesOnSaleDuring: 'esté en oferta durante',
      continueWithGoogle: 'Continuar con Google',
      instantNotifications: 'Notificaciones instantáneas cuando bajen los precios',
      exclusiveDeals: 'Ofertas exclusivas {eventType}',
      noSpam: 'Sin spam, solo las ofertas que quieres',
      agreementText: 'Al continuar, aceptas recibir alertas de precio para este producto.',
      unsubscribeText: 'Puedes darte de baja en cualquier momento.',
      successSubscribed: '¡Todo listo!',
      successUnsubscribed: 'Desuscripción exitosa',
      successSubscribedMessage: 'Te notificaremos cuando {productTitle} esté en oferta durante {eventName}',
      successUnsubscribedMessage: 'Ya no recibirás notificaciones para {productTitle} durante {eventName}',
      emailConfirmation: 'Revisa tu correo para confirmación',
      checkYourEmail: '💡 Revisa tu correo para confirmación',
      errorMessage: 'Error al actualizar la suscripción de alerta de precio',
    },
  },
  it: {
    suggestedPriceLabel: 'Prezzo suggerito',
    liveDealLabel: 'Offerta in tempo reale',
    blackFridayDealLabel: 'Offerta Black Friday',
    boxingDayDealLabel: 'Offerta Boxing Day',
    from: 'Da',
    priceAlerts: {
      notAvailable: 'Avvisi di prezzo non disponibili',
      signInToGet: 'Accedi per ricevere avvisi di prezzo {eventName}',
      turnOff: 'Disattiva avvisi di prezzo {eventName}',
      getNotified: 'Ricevi notifiche per le offerte {eventName}',
      title: 'Avviso prezzo {eventName}',
      neverMissADeal: 'Non perdere mai più un\'offerta',
      getNotifiedWhen: 'Ricevi una notifica nel momento in cui',
      goesOnSaleDuring: 'è in promozione durante',
      continueWithGoogle: 'Continua con Google',
      instantNotifications: 'Notifiche istantanee quando i prezzi scendono',
      exclusiveDeals: 'Offerte esclusive {eventType}',
      noSpam: 'Nessuno spam, solo le offerte che vuoi',
      agreementText: 'Continuando, accetti di ricevere avvisi di prezzo per questo prodotto.',
      unsubscribeText: 'Puoi annullare l\'iscrizione in qualsiasi momento.',
      successSubscribed: 'Tutto pronto!',
      successUnsubscribed: 'Disiscrizione completata',
      successSubscribedMessage: 'Ti avviseremo quando {productTitle} sarà in promozione durante {eventName}',
      successUnsubscribedMessage: 'Non riceverai più notifiche per {productTitle} durante {eventName}',
      emailConfirmation: 'Controlla la tua email per la conferma',
      checkYourEmail: '💡 Controlla la tua email per la conferma',
      errorMessage: 'Aggiornamento dell\'abbonamento all\'avviso di prezzo non riuscito',
    },
  },
  'pt-BR': {
    suggestedPriceLabel: 'Preço sugerido',
    liveDealLabel: 'Oferta ao vivo',
    blackFridayDealLabel: 'Oferta Black Friday',
    boxingDayDealLabel: 'Oferta Boxing Day',
    from: 'A partir de',
    priceAlerts: {
      notAvailable: 'Alertas de preço não disponíveis',
      signInToGet: 'Faça login para receber alertas de preço {eventName}',
      turnOff: 'Desativar alertas de preço {eventName}',
      getNotified: 'Receba notificações de ofertas {eventName}',
      title: 'Alerta de preço {eventName}',
      neverMissADeal: 'Nunca perca uma oferta novamente',
      getNotifiedWhen: 'Receba uma notificação no momento em que',
      goesOnSaleDuring: 'entrar em promoção durante',
      continueWithGoogle: 'Continuar com Google',
      instantNotifications: 'Notificações instantâneas quando os preços caírem',
      exclusiveDeals: 'Ofertas exclusivas {eventType}',
      noSpam: 'Sem spam, apenas as ofertas que você quer',
      agreementText: 'Ao continuar, você concorda em receber alertas de preço para este produto.',
      unsubscribeText: 'Você pode cancelar a inscrição a qualquer momento.',
      successSubscribed: 'Tudo pronto!',
      successUnsubscribed: 'Cancelamento bem-sucedido',
      successSubscribedMessage: 'Vamos te notificar quando {productTitle} entrar em promoção durante {eventName}',
      successUnsubscribedMessage: 'Você não receberá mais notificações para {productTitle} durante {eventName}',
      emailConfirmation: 'Verifique seu email para confirmação',
      checkYourEmail: '💡 Verifique seu email para confirmação',
      errorMessage: 'Falha ao atualizar a inscrição de alerta de preço',
    },
  },
  'pt-PT': {
    suggestedPriceLabel: 'Preço sugerido',
    liveDealLabel: 'Oferta em tempo real',
    blackFridayDealLabel: 'Oferta Black Friday',
    boxingDayDealLabel: 'Oferta Boxing Day',
    from: 'A partir de',
    priceAlerts: {
      notAvailable: 'Alertas de preço não disponíveis',
      signInToGet: 'Inicie sessão para receber alertas de preço {eventName}',
      turnOff: 'Desativar alertas de preço {eventName}',
      getNotified: 'Receba notificações de ofertas {eventName}',
      title: 'Alerta de preço {eventName}',
      neverMissADeal: 'Nunca perca uma oferta novamente',
      getNotifiedWhen: 'Receba uma notificação no momento em que',
      goesOnSaleDuring: 'entrar em promoção durante',
      continueWithGoogle: 'Continuar com Google',
      instantNotifications: 'Notificações instantâneas quando os preços descerem',
      exclusiveDeals: 'Ofertas exclusivas {eventType}',
      noSpam: 'Sem spam, apenas as ofertas que quer',
      agreementText: 'Ao continuar, concorda em receber alertas de preço para este produto.',
      unsubscribeText: 'Pode cancelar a subscrição a qualquer momento.',
      successSubscribed: 'Tudo pronto!',
      successUnsubscribed: 'Cancelamento bem-sucedido',
      successSubscribedMessage: 'Vamos notificá-lo quando {productTitle} entrar em promoção durante {eventName}',
      successUnsubscribedMessage: 'Não receberá mais notificações para {productTitle} durante {eventName}',
      emailConfirmation: 'Verifique o seu email para confirmação',
      checkYourEmail: '💡 Verifique o seu email para confirmação',
      errorMessage: 'Falha ao actualizar a subscrição de alerta de preço',
    },
  },
  ga: {
    suggestedPriceLabel: 'Praghas Molta',
    liveDealLabel: 'Margadh Beo',
    blackFridayDealLabel: 'Margadh Black Friday',
    boxingDayDealLabel: 'Margadh Boxing Day',
    from: 'Ó',
    priceAlerts: {
      notAvailable: 'Foláirimh phraghais nach bhfuil ar fáil',
      signInToGet: 'Sínigh isteach chun foláirimh phraghais {eventName} a fháil',
      turnOff: 'Cas as foláirimh phraghais {eventName}',
      getNotified: 'Faigh fógraí faoi mhargaí {eventName}',
      title: 'Foláireamh Praghais {eventName}',
      neverMissADeal: 'Ná caill margadh go deo arís',
      getNotifiedWhen: 'Faigh fógra an nóiméad a',
      goesOnSaleDuring: 'a théann ar díol le linn',
      continueWithGoogle: 'Lean ar aghaidh le Google',
      instantNotifications: 'Fógraí láithreacha nuair a thiteann praghsanna',
      exclusiveDeals: 'Margaí eisiatacha {eventType}',
      noSpam: 'Gan turscar, ach na margaí atá uait',
      agreementText: 'Trí leanúint ar aghaidh, aontaíonn tú foláirimh phraghais a fháil don táirge seo.',
      unsubscribeText: 'Is féidir leat díliostáil am ar bith.',
      successSubscribed: 'Tá Tú Réidh!',
      successUnsubscribed: 'Díliostáilte go Rathúil',
      successSubscribedMessage: 'Cuirfimid ar an eolas thú nuair a bheidh {productTitle} ar díol le linn {eventName}',
      successUnsubscribedMessage: 'Ní bhfaighidh tú fógraí do {productTitle} le linn {eventName} a thuilleadh',
      emailConfirmation: 'Seiceáil do ríomhphost le haghaidh deimhnithe',
      checkYourEmail: '💡 Seiceáil do ríomhphost le haghaidh deimhnithe',
      errorMessage: 'Theip ar shuibscríbhinn an fholáirimh phraghais a nuashonrú',
    },
  },
  sv: {
    suggestedPriceLabel: 'Rekommenderat pris',
    liveDealLabel: 'Aktuellt erbjudande',
    blackFridayDealLabel: 'Black Friday-erbjudande',
    boxingDayDealLabel: 'Boxing Day-erbjudande',
    from: 'Från',
    priceAlerts: {
      notAvailable: 'Prisaviseringar ej tillgängliga',
      signInToGet: 'Logga in för att få {eventName}-prisaviseringar',
      turnOff: 'Stäng av {eventName}-prisaviseringar',
      getNotified: 'Få aviseringar om {eventName}-erbjudanden',
      title: '{eventName}-prisavisering',
      neverMissADeal: 'Missa aldrig ett erbjudande igen',
      getNotifiedWhen: 'Få en avisering i samma ögonblick som',
      goesOnSaleDuring: 'går ner i pris under',
      continueWithGoogle: 'Fortsätt med Google',
      instantNotifications: 'Direktaviseringar vid prissänkningar',
      exclusiveDeals: 'Exklusiva {eventType}-erbjudanden',
      noSpam: 'Ingen spam, bara erbjudanden du vill ha',
      agreementText: 'Genom att fortsätta godkänner du att få prisaviseringar för denna produkt.',
      unsubscribeText: 'Du kan avsluta prenumerationen när som helst.',
      successSubscribed: 'Allt klart!',
      successUnsubscribed: 'Avprenumeration lyckades',
      successSubscribedMessage: 'Vi meddelar dig när {productTitle} går ner i pris under {eventName}',
      successUnsubscribedMessage: 'Du kommer inte längre att få aviseringar för {productTitle} under {eventName}',
      emailConfirmation: 'Kontrollera din e-post för bekräftelse',
      checkYourEmail: '💡 Kontrollera din e-post för bekräftelse',
      errorMessage: 'Det gick inte att uppdatera prisaviseringsprenumerationen',
    },
  },
  pl: {
    suggestedPriceLabel: 'Sugerowana cena',
    liveDealLabel: 'Aktualna oferta',
    blackFridayDealLabel: 'Oferta Black Friday',
    boxingDayDealLabel: 'Oferta Boxing Day',
    from: 'Od',
    priceAlerts: {
      notAvailable: 'Powiadomienia cenowe niedostępne',
      signInToGet: 'Zaloguj się, aby otrzymywać powiadomienia cenowe {eventName}',
      turnOff: 'Wyłącz powiadomienia cenowe {eventName}',
      getNotified: 'Otrzymuj powiadomienia o ofertach {eventName}',
      title: 'Powiadomienie cenowe {eventName}',
      neverMissADeal: 'Nigdy więcej nie przegap okazji',
      getNotifiedWhen: 'Otrzymaj powiadomienie, gdy',
      goesOnSaleDuring: 'będzie w promocji podczas',
      continueWithGoogle: 'Kontynuuj z Google',
      instantNotifications: 'Natychmiastowe powiadomienia o obniżkach cen',
      exclusiveDeals: 'Ekskluzywne oferty {eventType}',
      noSpam: 'Bez spamu, tylko wybrane oferty',
      agreementText: 'Kontynuując, wyrażasz zgodę na otrzymywanie powiadomień cenowych dla tego produktu.',
      unsubscribeText: 'Możesz zrezygnować z subskrypcji w dowolnym momencie.',
      successSubscribed: 'Wszystko gotowe!',
      successUnsubscribed: 'Pomyślnie wypisano',
      successSubscribedMessage: 'Powiadomimy Cię, gdy {productTitle} będzie w promocji podczas {eventName}',
      successUnsubscribedMessage: 'Nie będziesz już otrzymywać powiadomień o {productTitle} podczas {eventName}',
      emailConfirmation: 'Sprawdź e-mail w celu potwierdzenia',
      checkYourEmail: '💡 Sprawdź e-mail w celu potwierdzenia',
      errorMessage: 'Nie udało się zaktualizować subskrypcji powiadomień cenowych',
    },
  },
  gd: {
    suggestedPriceLabel: 'Prìs Mholte',
    liveDealLabel: 'Bargan Beò',
    blackFridayDealLabel: 'Bargan Black Friday',
    boxingDayDealLabel: 'Bargan Boxing Day',
    from: 'Bho',
    priceAlerts: {
      notAvailable: 'Chan eil rabhaidhean prìse ri fhaighinn',
      signInToGet: 'Clàraich a-steach gus rabhaidhean prìse {eventName} fhaighinn',
      turnOff: 'Cuir dheth rabhaidhean prìse {eventName}',
      getNotified: 'Faigh brath mu bharganan {eventName}',
      title: 'Rabhadh Prìse {eventName}',
      neverMissADeal: 'Na caill bargan a-chaoidh tuilleadh',
      getNotifiedWhen: 'Faigh brath sa bhad nuair a',
      goesOnSaleDuring: 'thèid air reic rè',
      continueWithGoogle: 'Lean air adhart le Google',
      instantNotifications: 'Brathan sa bhad nuair a thuiteas prìsean',
      exclusiveDeals: 'Barganan sònraichte {eventType}',
      noSpam: 'Gun spama, dìreach na barganan a tha thu ag iarraidh',
      agreementText: 'Le bhith a\' leantainn air adhart, tha thu ag aontachadh rabhaidhean prìse fhaighinn airson a\' bhathair seo.',
      unsubscribeText: 'Faodaidh tu dì-chlàrachadh aig àm sam bith.',
      successSubscribed: 'Tha thu deiseil!',
      successUnsubscribed: 'Dì-chlàrachadh soirbheachail',
      successSubscribedMessage: 'Bheir sinn fios dhut nuair a thèid {productTitle} air reic rè {eventName}',
      successUnsubscribedMessage: 'Cha bhi thu a\' faighinn bhrathan airson {productTitle} rè {eventName} tuilleadh',
      emailConfirmation: 'Thoir sùil air do phost-d airson dearbhadh',
      checkYourEmail: '💡 Thoir sùil air do phost-d airson dearbhadh',
      errorMessage: 'Dh\'fhàillig ùrachadh fo-sgrìobhadh rabhadh prìse',
    },
  },
  nl: {
    suggestedPriceLabel: 'Adviesprijs',
    liveDealLabel: 'Actuele aanbieding',
    blackFridayDealLabel: 'Black Friday-aanbieding',
    boxingDayDealLabel: 'Boxing Day-aanbieding',
    from: 'Vanaf',
    priceAlerts: {
      notAvailable: 'Prijsmeldingen niet beschikbaar',
      signInToGet: 'Meld u aan voor {eventName}-prijsmeldingen',
      turnOff: '{eventName}-prijsmeldingen uitschakelen',
      getNotified: 'Ontvang meldingen over {eventName}-aanbiedingen',
      title: '{eventName}-prijsmelding',
      neverMissADeal: 'Mis nooit meer een aanbieding',
      getNotifiedWhen: 'Ontvang een melding zodra',
      goesOnSaleDuring: 'in de aanbieding gaat tijdens',
      continueWithGoogle: 'Doorgaan met Google',
      instantNotifications: 'Directe meldingen bij prijsdalingen',
      exclusiveDeals: 'Exclusieve {eventType}-aanbiedingen',
      noSpam: 'Geen ongewenste berichten, alleen de aanbiedingen die u wilt',
      agreementText: 'Door verder te gaan, stemt u in met het ontvangen van prijsmeldingen voor dit product.',
      unsubscribeText: 'U kunt zich op elk moment afmelden.',
      successSubscribed: 'Alles is ingesteld!',
      successUnsubscribed: 'Afmelding geslaagd',
      successSubscribedMessage: 'Wij laten het u weten wanneer {productTitle} in de aanbieding gaat tijdens {eventName}',
      successUnsubscribedMessage: 'U ontvangt geen meldingen meer voor {productTitle} tijdens {eventName}',
      emailConfirmation: 'Controleer uw e-mail ter bevestiging',
      checkYourEmail: '💡 Controleer uw e-mail ter bevestiging',
      errorMessage: 'Het bijwerken van het prijsmeldingsabonnement is mislukt',
    },
  },
  te: {
    suggestedPriceLabel: 'సూచించిన ధర',
    liveDealLabel: 'ప్రత్యక్ష ఆఫర్',
    blackFridayDealLabel: 'Black Friday ఆఫర్',
    boxingDayDealLabel: 'Boxing Day ఆఫర్',
    from: 'నుండి',
    priceAlerts: {
      notAvailable: 'ధర హెచ్చరికలు అందుబాటులో లేవు',
      signInToGet: '{eventName} ధర హెచ్చరికలు పొందడానికి సైన్ ఇన్ చేయండి',
      turnOff: '{eventName} ధర హెచ్చరికలు ఆపివేయండి',
      getNotified: '{eventName} ఆఫర్ల గురించి తెలియజేయబడండి',
      title: '{eventName} ధర హెచ్చరిక',
      neverMissADeal: 'మళ్ళీ ఎప్పుడూ ఆఫర్ మిస్ కాకండి',
      getNotifiedWhen: 'తెలియజేయబడండి',
      goesOnSaleDuring: 'సమయంలో అమ్మకంలో ఉన్నప్పుడు',
      continueWithGoogle: 'Google తో కొనసాగించండి',
      instantNotifications: 'ధరలు తగ్గినప్పుడు తక్షణ నోటిఫికేషన్లు',
      exclusiveDeals: 'ప్రత్యేక {eventType} ఆఫర్లు',
      noSpam: 'స్పామ్ లేదు, మీకు కావలసిన ఆఫర్లు మాత్రమే',
      agreementText: 'కొనసాగించడం ద్వారా, ఈ ఉత్పత్తికి ధర హెచ్చరికలు అందుకోవడానికి మీరు అంగీకరిస్తున్నారు.',
      unsubscribeText: 'మీరు ఎప్పుడైనా అన్‌సబ్‌స్క్రైబ్ చేయవచ్చు.',
      successSubscribed: 'అంతా సిద్ధం!',
      successUnsubscribed: 'విజయవంతంగా అన్‌సబ్‌స్క్రైబ్ చేయబడింది',
      successSubscribedMessage: '{eventName} సమయంలో {productTitle} అమ్మకంలో ఉన్నప్పుడు మేము మీకు తెలియజేస్తాము',
      successUnsubscribedMessage: '{eventName} సమయంలో {productTitle} కోసం మీకు నోటిఫికేషన్లు అందవు',
      emailConfirmation: 'నిర్ధారణ కోసం మీ ఇమెయిల్ తనిఖీ చేయండి',
      checkYourEmail: '💡 నిర్ధారణ కోసం మీ ఇమెయిల్ తనిఖీ చేయండి',
      errorMessage: 'ధర హెచ్చరిక సబ్‌స్క్రిప్షన్ నవీకరించడంలో విఫలమైంది',
    },
  },
  gl: {
    suggestedPriceLabel: 'Prezo suxerido',
    liveDealLabel: 'Oferta en directo',
    blackFridayDealLabel: 'Oferta Black Friday',
    boxingDayDealLabel: 'Oferta Boxing Day',
    from: 'Desde',
    priceAlerts: {
      notAvailable: 'Alertas de prezo non dispoñíbeis',
      signInToGet: 'Inicie sesión para recibir alertas de prezo {eventName}',
      turnOff: 'Desactivar alertas de prezo {eventName}',
      getNotified: 'Reciba notificacións de ofertas {eventName}',
      title: 'Alerta de prezo {eventName}',
      neverMissADeal: 'Non perda nunca unha oferta',
      getNotifiedWhen: 'Reciba unha notificación no momento en que',
      goesOnSaleDuring: 'estea en oferta durante',
      continueWithGoogle: 'Continuar con Google',
      instantNotifications: 'Notificacións instantáneas cando os prezos baixan',
      exclusiveDeals: 'Ofertas exclusivas {eventType}',
      noSpam: 'Sen spam, só as ofertas que quere',
      agreementText: 'Ao continuar, acepta recibir alertas de prezo para este produto.',
      unsubscribeText: 'Pode cancelar a subscrición en calquera momento.',
      successSubscribed: 'Todo listo!',
      successUnsubscribed: 'Cancelación exitosa',
      successSubscribedMessage: 'Notificarémoslle cando {productTitle} estea en oferta durante {eventName}',
      successUnsubscribedMessage: 'Non recibirá máis notificacións para {productTitle} durante {eventName}',
      emailConfirmation: 'Comprobe o seu correo electrónico para confirmar',
      checkYourEmail: '💡 Comprobe o seu correo electrónico para confirmar',
      errorMessage: 'Erro ao actualizar a subscrición de alerta de prezo',
    },
  },
  mr: {
    suggestedPriceLabel: 'सुचवलेली किंमत',
    liveDealLabel: 'लाइव्ह डील',
    blackFridayDealLabel: 'Black Friday डील',
    boxingDayDealLabel: 'Boxing Day डील',
    from: 'पासून',
    priceAlerts: {
      notAvailable: 'किंमत सूचना उपलब्ध नाहीत',
      signInToGet: '{eventName} किंमत सूचना मिळवण्यासाठी साइन इन करा',
      turnOff: '{eventName} किंमत सूचना बंद करा',
      getNotified: '{eventName} डील्सच्या सूचना मिळवा',
      title: '{eventName} किंमत सूचना',
      neverMissADeal: 'पुन्हा कधीही डील चुकवू नका',
      getNotifiedWhen: 'सूचना मिळवा जेव्हा',
      goesOnSaleDuring: 'दरम्यान सवलतीवर येईल',
      continueWithGoogle: 'Google सह सुरू ठेवा',
      instantNotifications: 'किंमत कमी झाल्यावर तात्काळ सूचना',
      exclusiveDeals: 'विशेष {eventType} डील्स',
      noSpam: 'स्पॅम नाही, फक्त तुम्हाला हव्या असलेल्या डील्स',
      agreementText: 'पुढे सुरू ठेवल्यास, तुम्ही या उत्पादनासाठी किंमत सूचना प्राप्त करण्यास सहमत आहात.',
      unsubscribeText: 'तुम्ही कधीही सदस्यत्व रद्द करू शकता.',
      successSubscribed: 'सर्व तयार आहे!',
      successUnsubscribed: 'सदस्यत्व यशस्वीरीत्या रद्द केले',
      successSubscribedMessage: '{eventName} दरम्यान {productTitle} सवलतीवर आल्यावर आम्ही तुम्हाला सूचित करू',
      successUnsubscribedMessage: '{eventName} दरम्यान {productTitle} साठी तुम्हाला सूचना मिळणार नाहीत',
      emailConfirmation: 'पुष्टीकरणासाठी तुमचा ईमेल तपासा',
      checkYourEmail: '💡 पुष्टीकरणासाठी तुमचा ईमेल तपासा',
      errorMessage: 'किंमत सूचना सदस्यत्व अपडेट करण्यात अयशस्वी',
    },
  },
  ta: {
    suggestedPriceLabel: 'பரிந்துரைக்கப்பட்ட விலை',
    liveDealLabel: 'நேரடி சலுகை',
    blackFridayDealLabel: 'Black Friday சலுகை',
    boxingDayDealLabel: 'Boxing Day சலுகை',
    from: 'இருந்து',
    priceAlerts: {
      notAvailable: 'விலை எச்சரிக்கைகள் கிடைக்கவில்லை',
      signInToGet: '{eventName} விலை எச்சரிக்கைகளைப் பெற உள்நுழையுங்கள்',
      turnOff: '{eventName} விலை எச்சரிக்கைகளை முடக்குங்கள்',
      getNotified: '{eventName} சலுகைகளுக்கு அறிவிப்பு பெறுங்கள்',
      title: '{eventName} விலை எச்சரிக்கை',
      neverMissADeal: 'ஒரு சலுகையையும் தவறவிடாதீர்கள்',
      getNotifiedWhen: 'இந்த நேரத்தில் அறிவிப்பு பெறுங்கள்',
      goesOnSaleDuring: 'விற்பனையில் செல்லும்போது',
      continueWithGoogle: 'Google மூலம் தொடருங்கள்',
      instantNotifications: 'விலை குறையும்போது உடனடி அறிவிப்புகள்',
      exclusiveDeals: 'பிரத்யேக {eventType} சலுகைகள்',
      noSpam: 'ஸ்பேம் இல்லை, நீங்கள் விரும்பும் சலுகைகள் மட்டுமே',
      agreementText: 'தொடர்வதன் மூலம், இந்தப் பொருளுக்கான விலை எச்சரிக்கைகளைப் பெற ஒப்புக்கொள்கிறீர்கள்.',
      unsubscribeText: 'நீங்கள் எந்த நேரத்திலும் குழுவிலிருந்து விலகலாம்.',
      successSubscribed: 'அனைத்தும் தயார்!',
      successUnsubscribed: 'வெற்றிகரமாக குழுவிலிருந்து விலகப்பட்டது',
      successSubscribedMessage: '{eventName} போது {productTitle} விற்பனையில் செல்லும்போது நாங்கள் உங்களுக்கு அறிவிப்போம்',
      successUnsubscribedMessage: '{eventName} போது {productTitle} க்கான அறிவிப்புகளைப் பெறமாட்டீர்கள்',
      emailConfirmation: 'உறுதிப்படுத்தலுக்கு உங்கள் மின்னஞ்சலைச் சரிபாருங்கள்',
      checkYourEmail: '💡 உறுதிப்படுத்தலுக்கு உங்கள் மின்னஞ்சலைச் சரிபாருங்கள்',
      errorMessage: 'விலை எச்சரிக்கை சந்தாவைப் புதுப்பிப்பதில் தோல்வி',
    },
  },
  ca: {
    suggestedPriceLabel: 'Preu suggerit',
    liveDealLabel: 'Oferta en directe',
    blackFridayDealLabel: 'Oferta Black Friday',
    boxingDayDealLabel: 'Oferta Boxing Day',
    from: 'Des de',
    priceAlerts: {
      notAvailable: 'Alertes de preu no disponibles',
      signInToGet: 'Inicieu sessió per rebre alertes de preu {eventName}',
      turnOff: 'Desactivar alertes de preu {eventName}',
      getNotified: 'Rebeu notificacions d\'ofertes {eventName}',
      title: 'Alerta de preu {eventName}',
      neverMissADeal: 'No us perdeu mai més una oferta',
      getNotifiedWhen: 'Rebeu una notificació en el moment que',
      goesOnSaleDuring: 'estigui en promoció durant',
      continueWithGoogle: 'Continuar amb Google',
      instantNotifications: 'Notificacions instantànies quan baixin els preus',
      exclusiveDeals: 'Ofertes exclusives {eventType}',
      noSpam: 'Sense correu brossa, només les ofertes que voleu',
      agreementText: 'En continuar, accepteu rebre alertes de preu per a aquest producte.',
      unsubscribeText: 'Podeu donar-vos de baixa en qualsevol moment.',
      successSubscribed: 'Tot a punt!',
      successUnsubscribed: 'Baixa completada correctament',
      successSubscribedMessage: 'Us avisarem quan {productTitle} estigui en promoció durant {eventName}',
      successUnsubscribedMessage: 'No rebreu més notificacions per a {productTitle} durant {eventName}',
      emailConfirmation: 'Comproveu el vostre correu electrònic per a la confirmació',
      checkYourEmail: '💡 Comproveu el vostre correu electrònic per a la confirmació',
      errorMessage: 'No s\'ha pogut actualitzar la subscripció a l\'alerta de preu',
    },
  },
  mi: {
    suggestedPriceLabel: 'Utu Tūtohu',
    liveDealLabel: 'Whakaritenga Tūturu',
    blackFridayDealLabel: 'Whakaritenga Black Friday',
    boxingDayDealLabel: 'Whakaritenga Boxing Day',
    from: 'Mai i',
    priceAlerts: {
      notAvailable: 'Kāore e wātea ngā whakamōhio utu',
      signInToGet: 'Hainatia ki roto kia whiwhi whakamōhio utu {eventName}',
      turnOff: 'Whakawetohia ngā whakamōhio utu {eventName}',
      getNotified: 'Whiwhi whakamōhio mō ngā whakaritenga {eventName}',
      title: 'Whakamōhio Utu {eventName}',
      neverMissADeal: 'Kaua rawa e ngaro i tētahi whakaritenga anō',
      getNotifiedWhen: 'Whiwhi whakamōhio i te wā',
      goesOnSaleDuring: 'e hokona ana i te wā o',
      continueWithGoogle: 'Haere tonu me Google',
      instantNotifications: 'Whakamōhio tere ina heke ngā utu',
      exclusiveDeals: 'Whakaritenga motuhake {eventType}',
      noSpam: 'Kāore he tūkino, ko ngā whakaritenga anake e hiahia ana koe',
      agreementText: 'Mā te haere tonu, ka whakaae koe ki te whiwhi whakamōhio utu mō tēnei hua.',
      unsubscribeText: 'Ka taea e koe te whakakore i ngā ohauru ā te wā e hiahia ana.',
      successSubscribed: 'Kua Rite Katoa!',
      successUnsubscribed: 'Kua Whakakore Angitu',
      successSubscribedMessage: 'Ka whakamōhio mātou i a koe ina hokona a {productTitle} i te wā o {eventName}',
      successUnsubscribedMessage: 'Kāore koe e whiwhi whakamōhio mō {productTitle} i te wā o {eventName}',
      emailConfirmation: 'Tirohia tō īmēra hei whakaū',
      checkYourEmail: '💡 Tirohia tō īmēra hei whakaū',
      errorMessage: 'I hapa te whakahou i te ohauru whakamōhio utu',
    },
  },
  cy: {
    suggestedPriceLabel: 'Pris Awgrymedig',
    liveDealLabel: 'Bargen Fyw',
    blackFridayDealLabel: 'Bargen Black Friday',
    boxingDayDealLabel: 'Bargen Boxing Day',
    from: 'O',
    priceAlerts: {
      notAvailable: 'Nid yw rhybuddion pris ar gael',
      signInToGet: 'Mewngofnodwch i dderbyn rhybuddion pris {eventName}',
      turnOff: 'Diffodd rhybuddion pris {eventName}',
      getNotified: 'Cael eich hysbysu am fargeinion {eventName}',
      title: 'Rhybudd Pris {eventName}',
      neverMissADeal: 'Peidiwch byth â cholli bargen eto',
      getNotifiedWhen: 'Cael eich hysbysu y foment y mae',
      goesOnSaleDuring: 'ar werth yn ystod',
      continueWithGoogle: 'Parhau gyda Google',
      instantNotifications: 'Hysbysiadau ar unwaith pan fydd prisiau\'n gostwng',
      exclusiveDeals: 'Bargeinion {eventType} unigryw',
      noSpam: 'Dim sbam, dim ond y bargeinion rydych chi eu heisiau',
      agreementText: 'Drwy barhau, rydych yn cytuno i dderbyn rhybuddion pris ar gyfer y cynnyrch hwn.',
      unsubscribeText: 'Gallwch ddad-danysgrifio ar unrhyw adeg.',
      successSubscribed: 'Rydych yn Barod!',
      successUnsubscribed: 'Dad-danysgrifiwyd yn Llwyddiannus',
      successSubscribedMessage: 'Byddwn yn eich hysbysu pan fydd {productTitle} ar werth yn ystod {eventName}',
      successUnsubscribedMessage: 'Ni fyddwch yn derbyn hysbysiadau ar gyfer {productTitle} yn ystod {eventName} mwyach',
      emailConfirmation: 'Gwiriwch eich e-bost am gadarnhad',
      checkYourEmail: '💡 Gwiriwch eich e-bost am gadarnhad',
      errorMessage: 'Methwyd â diweddaru\'r tanysgrifiad rhybudd pris',
    },
  },
  eu: {
    suggestedPriceLabel: 'Iradokitako prezioa',
    liveDealLabel: 'Zuzeneko eskaintza',
    blackFridayDealLabel: 'Black Friday eskaintza',
    boxingDayDealLabel: 'Boxing Day eskaintza',
    from: 'Hemendik',
    priceAlerts: {
      notAvailable: 'Prezio-alertak ez daude erabilgarri',
      signInToGet: 'Hasi saioa {eventName} prezio-alertak jasotzeko',
      turnOff: 'Desaktibatu {eventName} prezio-alertak',
      getNotified: 'Jaso {eventName} eskaintzen jakinarazpenak',
      title: '{eventName} Prezio-alerta',
      neverMissADeal: 'Ez galdu eskaintzarik berriro',
      getNotifiedWhen: 'Jaso jakinarazpena une berean',
      goesOnSaleDuring: 'salgai dagoenean honako hau bitartean:',
      continueWithGoogle: 'Jarraitu Google-rekin',
      instantNotifications: 'Berehalako jakinarazpenak prezioak jaisten direnean',
      exclusiveDeals: '{eventType} eskaintza esklusiboak',
      noSpam: 'Spamik ez, nahi dituzun eskaintzak soilik',
      agreementText: 'Jarraitzean, produktu honetarako prezio-alertak jasotzea onartzen duzu.',
      unsubscribeText: 'Edozein unetan harpidetza bertan behera utzi dezakezu.',
      successSubscribed: 'Dena prest!',
      successUnsubscribed: 'Harpidetza bertan behera utzia',
      successSubscribedMessage: '{productTitle} {eventName} bitartean salgai jartzen denean jakinaraziko dizugu',
      successUnsubscribedMessage: 'Ez duzu {productTitle} produktuaren jakinarazpenik jasoko {eventName} bitartean',
      emailConfirmation: 'Egiaztatu zure emaila berrespenerako',
      checkYourEmail: '💡 Egiaztatu zure emaila berrespenerako',
      errorMessage: 'Ezin izan da prezio-alertaren harpidetza eguneratu',
    },
  },
  bn: {
    suggestedPriceLabel: 'প্রস্তাবিত মূল্য',
    liveDealLabel: 'লাইভ ডিল',
    blackFridayDealLabel: 'Black Friday ডিল',
    boxingDayDealLabel: 'Boxing Day ডিল',
    from: 'থেকে',
    priceAlerts: {
      notAvailable: 'মূল্য সতর্কতা উপলব্ধ নেই',
      signInToGet: '{eventName} মূল্য সতর্কতা পেতে সাইন ইন করুন',
      turnOff: '{eventName} মূল্য সতর্কতা বন্ধ করুন',
      getNotified: '{eventName} ডিলের বিজ্ঞপ্তি পান',
      title: '{eventName} মূল্য সতর্কতা',
      neverMissADeal: 'আর কখনও কোনো ডিল মিস করবেন না',
      getNotifiedWhen: 'সাথে সাথে বিজ্ঞপ্তি পান যখন',
      goesOnSaleDuring: 'বিক্রয়ে যায়',
      continueWithGoogle: 'Google দিয়ে চালিয়ে যান',
      instantNotifications: 'মূল্য কমলে তাৎক্ষণিক বিজ্ঞপ্তি',
      exclusiveDeals: 'একচেটিয়া {eventType} ডিল',
      noSpam: 'কোনো স্প্যাম নেই, শুধুমাত্র আপনার পছন্দের ডিল',
      agreementText: 'চালিয়ে যাওয়ার মাধ্যমে, আপনি এই পণ্যের মূল্য সতর্কতা পেতে সম্মত হচ্ছেন।',
      unsubscribeText: 'আপনি যেকোনো সময় সদস্যতা বাতিল করতে পারেন।',
      successSubscribed: 'সব প্রস্তুত!',
      successUnsubscribed: 'সফলভাবে সদস্যতা বাতিল হয়েছে',
      successSubscribedMessage: '{eventName}-এর সময় {productTitle} বিক্রয়ে গেলে আমরা আপনাকে জানাব',
      successUnsubscribedMessage: '{eventName}-এর সময় {productTitle}-এর জন্য আপনি আর বিজ্ঞপ্তি পাবেন না',
      emailConfirmation: 'নিশ্চিতকরণের জন্য আপনার ইমেইল দেখুন',
      checkYourEmail: '💡 নিশ্চিতকরণের জন্য আপনার ইমেইল দেখুন',
      errorMessage: 'মূল্য সতর্কতা সদস্যতা আপডেট করতে ব্যর্থ হয়েছে',
    },
  },
  hi: {
    suggestedPriceLabel: 'सुझावित मूल्य',
    liveDealLabel: 'लाइव डील',
    blackFridayDealLabel: 'Black Friday डील',
    boxingDayDealLabel: 'Boxing Day डील',
    from: 'से',
    priceAlerts: {
      notAvailable: 'मूल्य अलर्ट उपलब्ध नहीं हैं',
      signInToGet: '{eventName} मूल्य अलर्ट प्राप्त करने के लिए साइन इन करें',
      turnOff: '{eventName} मूल्य अलर्ट बंद करें',
      getNotified: '{eventName} डील की सूचना प्राप्त करें',
      title: '{eventName} मूल्य अलर्ट',
      neverMissADeal: 'फिर कभी कोई डील न चूकें',
      getNotifiedWhen: 'तुरंत सूचना प्राप्त करें जब',
      goesOnSaleDuring: 'के दौरान सेल पर आए',
      continueWithGoogle: 'Google से जारी रखें',
      instantNotifications: 'कीमत गिरने पर तुरंत सूचना',
      exclusiveDeals: 'विशेष {eventType} डील्स',
      noSpam: 'कोई स्पैम नहीं, केवल वे डील्स जो आप चाहते हैं',
      agreementText: 'जारी रखकर, आप इस उत्पाद के लिए मूल्य अलर्ट प्राप्त करने के लिए सहमत हैं।',
      unsubscribeText: 'आप किसी भी समय सदस्यता रद्द कर सकते हैं।',
      successSubscribed: 'सब तैयार है!',
      successUnsubscribed: 'सदस्यता सफलतापूर्वक रद्द हुई',
      successSubscribedMessage: '{eventName} के दौरान {productTitle} सेल पर आने पर हम आपको सूचित करेंगे',
      successUnsubscribedMessage: '{eventName} के दौरान {productTitle} के लिए आपको अब सूचनाएँ प्राप्त नहीं होंगी',
      emailConfirmation: 'पुष्टि के लिए अपना ईमेल देखें',
      checkYourEmail: '💡 पुष्टि के लिए अपना ईमेल देखें',
      errorMessage: 'मूल्य अलर्ट सदस्यता अपडेट करने में विफल',
    },
  },
  rm: {
    suggestedPriceLabel: 'Pretsch proponì',
    liveDealLabel: 'Purschida actuala',
    blackFridayDealLabel: 'Purschida Black Friday',
    boxingDayDealLabel: 'Purschida Boxing Day',
    from: 'Da',
    priceAlerts: {
      notAvailable: 'Avis da pretsch betg disponibels',
      signInToGet: 'S\'annunziai per survegnir avis da pretsch {eventName}',
      turnOff: 'Deactivar ils avis da pretsch {eventName}',
      getNotified: 'Vegnir infurmà davart purschidas {eventName}',
      title: 'Avis da pretsch {eventName}',
      neverMissADeal: 'Na manchentai mai pli ina purschida',
      getNotifiedWhen: 'Survegnì ina notificaziun il mument che',
      goesOnSaleDuring: 'è en acziun durant',
      continueWithGoogle: 'Cuntinuar cun Google',
      instantNotifications: 'Notificaziuns immediatadas cura che pretschs baissan',
      exclusiveDeals: 'Purschidas exclusivas {eventType}',
      noSpam: 'Nagin spam, mo las purschidas che Vus giavischais',
      agreementText: 'Cun cuntinuar acceptais Vus da survegnir avis da pretsch per quest product.',
      unsubscribeText: 'Vus pudais annullar l\'abunament da tut temp.',
      successSubscribed: 'Tut è pront!',
      successUnsubscribed: 'Annullaziun reussida',
      successSubscribedMessage: 'Nus As infurmain cura che {productTitle} è en acziun durant {eventName}',
      successUnsubscribedMessage: 'Vus na survegnis betg pli notificaziuns per {productTitle} durant {eventName}',
      emailConfirmation: 'Controllai Voss e-mail per la conferma',
      checkYourEmail: '💡 Controllai Voss e-mail per la conferma',
      errorMessage: 'Actualisaziun da l\'abunament d\'avis da pretsch n\'è betg reussida',
    },
  },
  ja: {
    suggestedPriceLabel: '参考価格',
    liveDealLabel: 'タイムセール',
    blackFridayDealLabel: 'ブラックフライデーセール',
    boxingDayDealLabel: 'ボクシングデーセール',
    from: '￥',
    priceAlerts: {
      notAvailable: '価格アラートはご利用いただけません',
      signInToGet: 'ログインして{eventName}の価格アラートを受け取る',
      turnOff: '{eventName}の価格アラートをオフにする',
      getNotified: '{eventName}のお得情報を通知で受け取る',
      title: '{eventName}価格アラート',
      neverMissADeal: 'もうセールを見逃さない',
      getNotifiedWhen: '値下げの瞬間に通知を受け取る',
      goesOnSaleDuring: 'がセール対象になったとき（期間：',
      continueWithGoogle: 'Googleで続行',
      instantNotifications: '価格が下がった瞬間にお知らせ',
      exclusiveDeals: '{eventType}限定セール',
      noSpam: 'スパムなし。欲しい商品のお得情報だけをお届け',
      agreementText: '続行することで、この商品の価格アラートを受け取ることに同意したものとみなされます。',
      unsubscribeText: 'いつでも配信を停止できます。',
      successSubscribed: '設定完了！',
      successUnsubscribed: '配信停止が完了しました',
      successSubscribedMessage: '{eventName}期間中に{productTitle}がセールになったらお知らせします',
      successUnsubscribedMessage: '{eventName}期間中の{productTitle}に関する通知は届かなくなります',
      emailConfirmation: '確認メールをご確認ください',
      checkYourEmail: '💡 確認メールをご確認ください',
      errorMessage: '価格アラートの登録を更新できませんでした',
    },
  },
};

export const priceUiContent: Record<LanguageType, PriceLocaleRow> =
  proxyLanguageRecord<PriceLocaleRow>(priceUiContentCore);

// ISO 4217 currency code per region. Used both for display ("CAD") and for
// `priceCurrency` in inline schema.org/Offer microdata so AI crawlers can bind
// the visible number to a currency without guessing.
export const currencyByRegion: Record<RegionCode, string> = {
  CA: 'CAD',
  US: 'USD',
  FR: 'EUR',
  UK: 'GBP',
  AU: 'AUD',
  BR: 'BRL',
  PT: 'EUR',
  DE: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  MX: 'MXN',
  CH: 'CHF',
  AT: 'EUR',
  BE: 'EUR',
  NL: 'EUR',
  PL: 'PLN',
  SE: 'SEK',
  IE: 'EUR',
  NZ: 'NZD',
  IN: 'INR',
  AR: 'ARS',
  CO: 'COP',
  CL: 'CLP',
  JP: 'JPY',
  ALL: 'USD',
};

// Display symbol per region. Kept short so the visible price line stays compact.
export const currencySymbolByRegion: Record<RegionCode, string> = {
  CA: '$',
  US: '$',
  FR: '€',
  UK: '£',
  AU: 'A$',
  BR: 'R$',
  PT: '€',
  DE: '€',
  ES: '€',
  IT: '€',
  MX: 'MX$',
  CH: 'CHF',
  AT: '€',
  BE: '€',
  NL: '€',
  PL: 'zł',
  SE: 'kr',
  IE: '€',
  NZ: 'NZ$',
  IN: '₹',
  AR: 'AR$',
  CO: 'COL$',
  CL: 'CLP$',
  JP: '¥',
  ALL: '$',
};

export const getCurrencyCode = (country?: RegionCode): string =>
  currencyByRegion[country ?? RegionCode.Canada] ?? 'USD';

export const getCurrencySymbol = (country?: RegionCode): string =>
  currencySymbolByRegion[country ?? RegionCode.Canada] ?? '$';

// Symbol-after regions (Swedish kr, Polish zł) for the locale-less format.
const SYMBOL_AFTER: ReadonlySet<RegionCode> = new Set([
  RegionCode.Sweden,
  RegionCode.Poland,
]);

const NBSP = '\u00a0'; // no-break space
const NNBSP = '\u202f'; // narrow no-break space (French thousands)

// How one locale writes an amount of money. OUR table, not Intl's: Intl's
// output is CLDR data that each engine ships at its own version, and the
// labels are rendered on the server and hydrated in the browser. Measured
// 2026-09: 18 of our 67 locale paths print differently between Node, Chrome
// and Safari (en-FR "€26.27" in Node, "€26,27" in Safari; fr-CH changed
// between Node 22 and 24), and any difference is a hydration error and a
// full client re-render. A table cannot drift; the symbol stays the region's
// (`currencySymbolByRegion`), only its side and spacing come from here.
type MoneyFormat = {
  /** Between whole units and cents. */
  decimal: '.' | ',';
  /** Between groups of three (or two, `indian`) whole-unit digits. */
  group: string;
  /** "26,27 $" rather than "$26.27". */
  symbolAfter: boolean;
  /** Between number and symbol; '' = touching. A symbol that would touch the
   *  number with a letter ("CHF1") gets a no-break space regardless. */
  gap: string;
  /** Lakh grouping, the Indian convention: "12,34,567.89". */
  indian?: true;
};

// $1,790.00 — English, and what every locale printed before this table.
const EN: MoneyFormat = { decimal: '.', group: ',', symbolAfter: false, gap: '' };
// 1,790.00 kr — English figures, Swedish/Polish symbol side.
const EN_AFTER: MoneyFormat = { decimal: '.', group: ',', symbolAfter: true, gap: NBSP };
// ₹12,34,567.89
const EN_IN: MoneyFormat = { ...EN, indian: true };
// 1 790,00 € — French (narrow no-break space thousands).
const FR: MoneyFormat = { decimal: ',', group: NNBSP, symbolAfter: true, gap: NBSP };
// 1 790,00 kr — fr-CA, Swedish, Polish, European Portuguese.
const SPACE_AFTER: MoneyFormat = { decimal: ',', group: NBSP, symbolAfter: true, gap: NBSP };
// 1.790,00 € — German, Spanish (Spain), Italian, Catalan, Galician, Basque.
const DOT_AFTER: MoneyFormat = { decimal: ',', group: '.', symbolAfter: true, gap: NBSP };
// € 1.790,00 — Dutch, Austrian German, Brazilian, Argentine, Colombian.
const DOT_BEFORE: MoneyFormat = { decimal: ',', group: '.', symbolAfter: false, gap: NBSP };
// CLP$1.790,00 — Chilean.
const DOT_BEFORE_TIGHT: MoneyFormat = { decimal: ',', group: '.', symbolAfter: false, gap: '' };
// CHF 1’790.00 — Swiss, in every Swiss language.
const SWISS: MoneyFormat = { decimal: '.', group: '\u2019', symbolAfter: false, gap: NBSP };

// Language-level default: what a locale path gets unless listed below. English
// abroad (en-FR, en-DE, en-BR...) keeps English figures: the reader chose
// English, and it is what those pages always printed.
const MONEY_FORMAT_BY_LANGUAGE: ReadonlyMap<string, MoneyFormat> = new Map([
  ['en', EN],
  ['cy', EN],
  ['ga', EN],
  ['gd', EN],
  ['mi', EN],
  ['ja', EN],
  ['hi', EN_IN],
  ['bn', EN_IN],
  ['mr', EN_IN],
  ['ta', EN_IN],
  ['te', EN_IN],
  ['fr', FR],
  ['de', DOT_AFTER],
  ['es', DOT_AFTER],
  ['it', DOT_AFTER],
  ['ca', DOT_AFTER],
  ['gl', DOT_AFTER],
  ['eu', DOT_AFTER],
  ['nl', DOT_BEFORE],
  ['pt', SPACE_AFTER],
  ['pl', SPACE_AFTER],
  ['sv', SPACE_AFTER],
  ['rm', SWISS],
]);

// Locale paths whose country writes money differently from the language
// default.
const MONEY_FORMAT_BY_LOCALE: ReadonlyMap<string, MoneyFormat> = new Map([
  ['fr-CA', SPACE_AFTER],
  ['fr-CH', SWISS],
  ['de-CH', SWISS],
  ['it-CH', SWISS],
  ['de-AT', DOT_BEFORE],
  ['pt-BR', DOT_BEFORE],
  ['es-AR', DOT_BEFORE],
  ['es-CO', DOT_BEFORE],
  ['es-CL', DOT_BEFORE_TIGHT],
  ['es-MX', EN],
  ['es-US', EN],
  // Priced in US dollars for a mostly American readership: written the way
  // the US and Mexico write it, not Spain.
  ['es-ALL', EN],
  ['en-IN', EN_IN],
  ['en-SE', EN_AFTER],
  ['en-PL', EN_AFTER],
]);

const moneyFormatFor = (localePath: string): MoneyFormat | null =>
  MONEY_FORMAT_BY_LOCALE.get(localePath) ??
  MONEY_FORMAT_BY_LANGUAGE.get(localePath.split('-')[0].toLowerCase()) ??
  null;

// "1234567" → "1,234,567" (or "12,34,567" in lakh grouping).
const groupDigits = (digits: string, separator: string, indian: boolean): string => {
  if (digits.length <= 3) return digits;
  const groups = [digits.slice(-3)];
  const size = indian ? 2 : 3;
  let rest = digits.slice(0, -3);
  while (rest.length > size) {
    groups.unshift(rest.slice(-size));
    rest = rest.slice(0, -size);
  }
  groups.unshift(rest);
  return groups.join(separator);
};

// The amount in the locale's own figures, or null when this table does not
// know the locale (the caller falls back). Rounding shifts the decimal point
// in the number's shortest decimal form ("1.005e2") instead of multiplying
// (1.005 * 100 = 100.49999999999999), so a half cent rounds up the way it
// reads, which is also how the en-US fallback rounds. Every step is exact
// ECMAScript, so every engine prints the same string.
const formatWithLocale = (
  num: number,
  localePath: string,
  symbol: string,
  fractionDigits: number,
): string | null => {
  const format = moneyFormatFor(localePath);
  if (!format) return null;

  const abs = Math.abs(num);
  const shifted = Number(`${abs}e${fractionDigits}`);
  const minor = Math.round(Number.isFinite(shifted) ? shifted : abs * 10 ** fractionDigits);
  if (!Number.isSafeInteger(minor)) return null;

  const digits = String(minor).padStart(fractionDigits + 1, '0');
  const whole = fractionDigits > 0 ? digits.slice(0, -fractionDigits) : digits;
  const cents = fractionDigits > 0 ? digits.slice(-fractionDigits) : '';
  const amount =
    groupDigits(whole, format.group, format.indian === true) + (cents ? format.decimal + cents : '');

  const touchesLetter = format.symbolAfter ? /^\p{L}/u.test(symbol) : /\p{L}$/u.test(symbol);
  const gap = format.gap || (touchesLetter ? NBSP : '');
  const sign = num < 0 && minor > 0 ? '-' : '';
  return format.symbolAfter ? `${sign}${amount}${gap}${symbol}` : `${sign}${symbol}${gap}${amount}`;
};

// Returns a human-readable price. With the page's `localePath` ("fr-CA",
// "sv-SE", ...) it is written the way that locale writes money: "26,27 $",
// "1 790,00 kr", "26,27 €", "¥1,790" (MONEY_FORMAT_BY_* above). Without one,
// or for a path the table does not know, it is the en-US-grouped format every
// caller got before locales were threaded through: "$1,790.00", "1,790.00 kr",
// "¥1,790" — byte for byte, so a caller that passes no locale never changes.
// Currency disambiguation (CAD/USD/EUR/...) is rendered separately by the
// price block so it can sit on its own line and not crowd the price itself.
export const priceByCountry = (
  price?: number | string,
  country?: RegionCode,
  localePath?: string,
) => {
  if (price === undefined || price === null || price === '') return '';
  const num = +price;
  if (!Number.isFinite(num)) return '';

  const region = country ?? RegionCode.Canada;
  const symbol = getCurrencySymbol(region);
  // JPY has no decimal subunit.
  const fractionDigits = region === RegionCode.Japan ? 0 : 2;

  if (typeof localePath === 'string' && localePath) {
    const localized = formatWithLocale(num, localePath, symbol, fractionDigits);
    if (localized !== null) return localized;
  }

  const amount = num.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  if (SYMBOL_AFTER.has(region)) return `${amount} ${symbol}`;
  return `${symbol}${amount}`;
};

// Helper function to replace placeholders in strings
export const replacePlaceholders = (text: string, replacements: Record<string, string>): string => {
  return Object.entries(replacements).reduce((acc, [key, value]) => acc.replace(new RegExp(`{${key}}`, 'g'), value), text);
};
